import { getCourseScheduleDates, getPublicCourseSchedule, type WebsiteScheduleEvent } from "./course-schedule";
import { differenceInCalendarDays, isSameDay, startOfDay } from "date-fns";
import { filterClassScheduleDates } from "@/utils/theory-exam-schedule";
import {
  formatInLondon,
  parseGraphqlTimestamptz,
} from "@/utils/uk-datetime";

export type CourseEventSchedule = WebsiteScheduleEvent & {
  date?: string | null;
  start?: string | null;
  end?: string | null;
  vtctSettings?: {
    dates?: Array<{
      start?: string | null;
      end?: string | null;
      label?: string | null;
    }> | null;
  } | null;
};

export type CourseEvent = CourseEventSchedule & {
  id: string;
  seats?: number | null;
  price?: number | null;
  registrations?: {
    aggregate?: { count?: number | null } | null;
  } | null;
  countStudentRegistrations?: {
    aggregate?: { count?: number | null } | null;
  } | null;
  detail?: {
    contentful_id?: string | null;
    title?: string | null;
    attributes?: { category?: string | null } | null;
  } | null;
};

export type ContentfulCoursePage = {
  sys?: { id?: string };
  fields?: {
    url?: string | null;
    listingImage?: {
      fields?: {
        file?: { url?: string | null };
      };
    } | null;
  };
};

export type StartingSoonCourse = {
  eventId: string;
  title: string;
  category: string;
  startIso: string;
  endIso: string | null;
  priceLabel: string | null;
  seatsAvailable: number;
  totalSeats: number;
  courseUrl: string | null;
  imageUrl: string | null;
  dateMonth: string;
  dateDay: string;
  startLabel: string;
  timeRange: string;
  seatsLabel: string;
  seatsTone: "urgent" | "limited" | "available";
};

function getClassScheduleDates(event: CourseEventSchedule | null | undefined) {
  return getCourseScheduleDates(event);
}

export function getEventStart(event: CourseEventSchedule | null | undefined) {
  const result = getPublicCourseSchedule(event);
  return result.ok ? result.schedule.firstTeachingStart : null;
}

export function getEventEnd(event: CourseEventSchedule | null | undefined) {
  const result = getPublicCourseSchedule(event);
  return result.ok ? result.schedule.lastDeliveryEnd : null;
}

function formatScheduleDateRange(start: Date, end: Date) {
  if (isSameDay(start, end)) {
    return formatInLondon(start, "do MMMM yyyy");
  }

  const startMonth = formatInLondon(start, "MM");
  const endMonth = formatInLondon(end, "MM");
  const startYear = formatInLondon(start, "yyyy");
  const endYear = formatInLondon(end, "yyyy");
  const sameMonth = startMonth === endMonth && startYear === endYear;
  const sameYear = startYear === endYear;

  if (sameMonth) {
    return `${formatInLondon(start, "do")} - ${formatInLondon(end, "do MMMM yyyy")}`;
  }

  if (sameYear) {
    return `${formatInLondon(start, "do MMMM")} - ${formatInLondon(end, "do MMMM yyyy")}`;
  }

  return `${formatInLondon(start, "do MMMM yyyy")} - ${formatInLondon(end, "do MMMM yyyy")}`;
}

function formatScheduleTimes(start: Date, end: Date | null) {
  if (!end || isSameDay(start, end)) {
    return `${formatInLondon(start, "HH:mm")}${
      end ? ` - ${formatInLondon(end, "HH:mm")}` : ""
    }`;
  }

  return null;
}

export function formatTrainingCourseSchedule(event: CourseEventSchedule | null | undefined): string | null {
  const result = getPublicCourseSchedule(event);
  if (!result.ok) return null;
  const dates = getClassScheduleDates(event).filter(row => row.kind !== "EXTERNAL_ASSESSMENT" && row.state === "CONFIRMED");
  if (!dates.length) return null;
  const first = parseGraphqlTimestamptz(dates[0].start);
  const last = parseGraphqlTimestamptz(dates[dates.length - 1].start);
  if (dates.length === 1) return `${formatInLondon(first, "do MMMM yyyy")} · ${formatScheduleTimes(first, parseGraphqlTimestamptz(dates[0].end))}`;
  const counts = result.schedule.counts;
  const description = [counts.webinarDays ? `${counts.webinarDays} webinar${counts.webinarDays === 1 ? "" : "s"}` : "", counts.onsiteDays ? `${counts.onsiteDays} onsite day${counts.onsiteDays === 1 ? "" : "s"}` : ""].filter(Boolean).join(" · ");
  return `${formatScheduleDateRange(first, last)} · ${description || `${dates.length} teaching contacts`}`;
}

export function getEventCategory(event: CourseEvent | null | undefined) {
  return event?.detail?.attributes?.category || "";
}

export function getRegistrationCount(event: CourseEvent | null | undefined) {
  return (
    event?.countStudentRegistrations?.aggregate?.count ??
    event?.registrations?.aggregate?.count ??
    0
  );
}

export function getAvailableSeats(event: CourseEvent | null | undefined) {
  return Math.max(0, (event?.seats || 0) - getRegistrationCount(event));
}

export function resolveCoursePageUrl(
  contentItem: ContentfulCoursePage | null | undefined,
) {
  const url = contentItem?.fields?.url;
  if (!url) {
    return null;
  }

  return url.startsWith("/") ? url : `/${url}`;
}

export function resolveCourseImageUrl(
  contentItem: ContentfulCoursePage | null | undefined,
) {
  const raw = contentItem?.fields?.listingImage?.fields?.file?.url;
  if (!raw) {
    return null;
  }

  return raw.startsWith("//") ? `https:${raw}` : raw;
}

export function findContentfulCoursePage(
  contentItems: ContentfulCoursePage[] | null | undefined,
  contentfulId: string | null | undefined,
) {
  if (!contentfulId || !contentItems?.length) {
    return null;
  }

  return contentItems.find((item) => item.sys?.id === contentfulId) ?? null;
}
